import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  MessageSquare, Send, Loader2, ArrowLeft, User as UserIcon, Check, X,
  Phone, Tag as TagIcon, Mail, Clock, CornerDownRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) {
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

const maskPhone = (e164) => {
  if (!e164) return '—';
  return e164.replace(/^(\+\d)(\d{3})(\d{3})(\d+)$/, '$1 ($2) $3-$4');
};

export default function SMSInbox() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [convs, setConvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [thread, setThread] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState('open');
  const threadEndRef = useRef(null);

  useEffect(() => { fetchConvs(); /* eslint-disable-next-line */ }, [filter]);
  useEffect(() => { if (selectedPhone) fetchThread(selectedPhone); /* eslint-disable-next-line */ }, [selectedPhone]);
  useEffect(() => { threadEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [thread?.messages?.length]);

  const fetchConvs = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/sms-inbox?status=${filter}`, { headers });
      setConvs(r.data.conversations || []);
    } catch { toast.error('Failed to load inbox'); }
    finally { setLoading(false); }
  };

  const fetchThread = async (phone) => {
    setThreadLoading(true);
    try {
      const r = await axios.get(`${API}/admin/sms-inbox/${encodeURIComponent(phone)}`, { headers });
      setThread(r.data);
      // Refresh inbox to clear the unread badge
      fetchConvs();
    } catch { toast.error('Failed to load thread'); }
    finally { setThreadLoading(false); }
  };

  const sendReply = async () => {
    if (!reply.trim() || !selectedPhone) return;
    setSending(true);
    try {
      await axios.post(
        `${API}/admin/sms-inbox/${encodeURIComponent(selectedPhone)}/reply`,
        { body: reply },
        { headers },
      );
      setReply('');
      await fetchThread(selectedPhone);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.response?.data?.error || 'Send failed');
    } finally { setSending(false); }
  };

  const resolve = async () => {
    if (!selectedPhone) return;
    try {
      await axios.post(`${API}/admin/sms-inbox/${encodeURIComponent(selectedPhone)}/resolve`, {}, { headers });
      toast.success('Conversation resolved');
      setSelectedPhone(null); setThread(null);
      await fetchConvs();
    } catch { toast.error('Could not resolve'); }
  };

  return (
    <div className="min-h-screen bg-background pt-20 pb-12" data-testid="sms-inbox-page">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="flex items-center gap-3 mb-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
          <h1 className="text-2xl font-bold flex items-center gap-2"><MessageSquare className="h-6 w-6 text-indigo-400" /> SMS Inbox</h1>
        </div>

        <div className="grid lg:grid-cols-12 gap-4">
          {/* Conversation list */}
          <Card className="lg:col-span-4 h-[calc(100vh-180px)]">
            <CardContent className="p-0 h-full flex flex-col">
              <div className="border-b p-3 flex items-center gap-2">
                {['open', 'resolved', 'all'].map(f => (
                  <Button
                    key={f}
                    size="sm"
                    variant={filter === f ? 'default' : 'ghost'}
                    className="h-7 text-xs capitalize"
                    onClick={() => setFilter(f)}
                  >{f}</Button>
                ))}
              </div>
              <div className="overflow-y-auto flex-1">
                {loading ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin" /></div>
                : convs.length === 0 ? <div className="text-center py-8 text-sm text-muted-foreground">No conversations yet.</div>
                : convs.map(c => (
                  <button
                    key={c.phone}
                    onClick={() => setSelectedPhone(c.phone)}
                    className={`w-full text-left px-3 py-3 border-b hover:bg-muted/40 transition-colors ${selectedPhone === c.phone ? 'bg-muted/60' : ''}`}
                    data-testid={`sms-conv-${c.phone.replace(/[^0-9]/g, '')}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-sm truncate">
                        {c.linked_user_name || maskPhone(c.phone)}
                      </span>
                      {c.unread_count > 0 && (
                        <Badge className="bg-indigo-500 text-white text-[10px] h-4">{c.unread_count}</Badge>
                      )}
                    </div>
                    <p className="text-[11px] text-muted-foreground truncate mt-0.5">
                      {c.last_direction === 'outbound' && <CornerDownRight className="h-2.5 w-2.5 inline mr-0.5" />}
                      {c.last_message}
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{fmtTime(c.last_at)}</p>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Thread */}
          <Card className="lg:col-span-8 h-[calc(100vh-180px)]">
            <CardContent className="p-0 h-full flex flex-col">
              {!selectedPhone ? (
                <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
                  Pick a conversation to view the thread.
                </div>
              ) : threadLoading ? (
                <div className="flex-1 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>
              ) : !thread ? null : (
                <>
                  {/* Header */}
                  <div className="border-b p-3 flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="font-semibold text-sm flex items-center gap-2">
                        <Phone className="h-3.5 w-3.5" />
                        {thread.contact?.name || maskPhone(thread.conversation.phone)}
                      </div>
                      <div className="text-[11px] text-muted-foreground flex items-center gap-2 flex-wrap">
                        <span>{maskPhone(thread.conversation.phone)}</span>
                        {thread.contact?.email && (<><span>·</span><Mail className="h-3 w-3" /> {thread.contact.email}</>)}
                        {thread.contact?.crm_label && (<><span>·</span><TagIcon className="h-3 w-3" /> {thread.contact.crm_label}</>)}
                      </div>
                      {thread.contact?.crm_tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {thread.contact.crm_tags.slice(0, 6).map(t => (
                            <Badge key={t} variant="outline" className="text-[9px] py-0 h-4">{t}</Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {thread.contact?.user_id && (
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => navigate(`/admin/crm?contact=${thread.contact.user_id}`)}>
                          <UserIcon className="h-3 w-3 mr-1" /> CRM
                        </Button>
                      )}
                      {thread.conversation.status === 'open' && (
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={resolve} data-testid="sms-resolve-btn">
                          <Check className="h-3 w-3 mr-1" /> Resolve
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-2">
                    {thread.messages.map(m => (
                      <div
                        key={m.message_id}
                        className={`flex ${m.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`max-w-[70%] rounded-2xl px-3 py-2 text-sm ${
                          m.direction === 'outbound'
                            ? 'bg-indigo-600 text-white rounded-br-sm'
                            : 'bg-muted text-foreground rounded-bl-sm'
                        }`}>
                          {m.media_url && (
                            <img src={m.media_url} alt="" className="rounded-lg mb-1 max-h-48 w-full object-cover" />
                          )}
                          <div className="whitespace-pre-wrap">{m.body}</div>
                          <div className={`text-[9px] mt-1 ${m.direction === 'outbound' ? 'text-indigo-100' : 'text-muted-foreground'}`}>
                            {fmtTime(m.created_at)}
                            {m.status && m.direction === 'outbound' && (
                              <> · <span className="capitalize">{m.status.replace('_', ' ')}</span></>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    <div ref={threadEndRef} />
                  </div>

                  {/* Reply */}
                  <div className="border-t p-3 flex items-end gap-2">
                    <Textarea
                      rows={2}
                      value={reply}
                      onChange={e => setReply(e.target.value)}
                      placeholder="Reply…"
                      className="resize-none flex-1"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); sendReply(); }
                      }}
                      data-testid="sms-reply-input"
                    />
                    <Button onClick={sendReply} disabled={sending || !reply.trim()} data-testid="sms-reply-btn">
                      {sending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                    </Button>
                  </div>
                  <p className="text-[10px] text-muted-foreground px-3 pb-2">⌘+Enter to send. Admin replies bypass marketing opt-out (per-thread conversation, not marketing).</p>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
